/**
 * 여러 탭의 쓰기 소유권 lease (TASK-2026-004 SPEC 「포함 범위」 4, 중앙 명세 F1-05).
 *
 * ## 문제
 *
 * 한 브라우저에서 같은 참가 세션을 두 탭이 열 수 있다. 새로고침 대신 링크를 다시
 * 누르거나, 게임 탭을 남긴 채 설문 탭을 여는 경우다. 두 탭이 같은 로그를 읽어 같은
 * 순번을 서로 보내면, 서버는 같은 구간을 다른 batch ID로 받아 `seq_conflict` 409를
 * 내고 한쪽 탭은 계속 실패한다. 더 나쁜 경우는 두 탭이 서로 다른 시도를 동시에
 * 기록해 한 세션의 기록이 뒤섞이는 것이다.
 *
 * ## 규칙
 *
 * 쓰는 탭은 **하나**다. localStorage에 임차 기록(holder + 갱신 시각)을 두고,
 * 가진 탭만 전송한다. 가진 탭은 주기적으로 갱신하고, 비어 있거나 만료된 기록은
 * 다른 탭이 가져갈 수 있다. 창을 닫을 때 놓아 주면 다음 탭이 즉시 가져간다.
 *
 * 가지지 못한 탭은 **아무것도 보내지 않고** 읽기·재개 안내를 보여 준다. 쓰기를
 * 막는 편이 두 탭이 번갈아 실패하는 것보다 설명하기 쉽다.
 *
 * ## 왜 Web Locks도 BroadcastChannel도 쓰지 않는가
 *
 * 둘 다 새 의존성은 아니지만 지원 범위가 갈리고(특히 구형 iOS Safari),
 * localStorage는 이미 계측 로그가 쓰는 저장소다. 이 파일은 `Date.now()`와
 * 저장소만 쓴다. 저장소가 막힌 환경에서는 lease를 유지할 수 없으므로 **그 탭은
 * 소유자가 되지 못한다** — 저장할 수 없는 곳에서 조용히 두 탭이 다 쓰는 것보다
 * 낫다.
 */

export const LEASE_KEY = 'ccatlas.online.lease';

/** 이 시간 동안 갱신이 없으면 임차가 만료된다. */
export const LEASE_TTL_MS = 8_000;

/** 소유자가 갱신하는 간격. TTL의 절반보다 짧게 둔다. */
export const LEASE_RENEW_MS = 3_000;

export interface LeaseStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface LeaseRecord {
  readonly holder: string;
  readonly renewed_at: number;
  /** 어느 세션의 쓰기 임차인가. 다른 세션이면 가져갈 수 있다. */
  readonly session_ref: string;
}

export interface LeaseOptions {
  readonly storage: LeaseStorage;
  /** 이 탭의 식별자. 저장하지 않고 이번 실행 동안만 쓰는 값이다. */
  readonly tabId: string;
  readonly sessionRef: string;
  readonly now: () => number;
}

export interface Lease {
  /** 지금 쓰기 소유자인가. 필요하면 가져온다. */
  acquire(): boolean;
  /** 이미 가진 임차를 갱신한다. 남이 가져갔으면 `false`. */
  renew(): boolean;
  /** 소유 상태를 바꾸지 않고 확인만 한다. */
  held(): boolean;
  /** 놓아 준다. 남의 임차는 지우지 않는다. */
  release(): void;
  /** 다른 탭이 쓰고 있는가(내 것이 아니고 만료도 아니다). */
  heldByOther(): boolean;
}

function parse(raw: string | null): LeaseRecord | undefined {
  if (raw === null) {
    return undefined;
  }
  try {
    const value = JSON.parse(raw) as Partial<LeaseRecord>;
    if (
      typeof value.holder !== 'string' ||
      typeof value.session_ref !== 'string' ||
      typeof value.renewed_at !== 'number' ||
      !Number.isFinite(value.renewed_at)
    ) {
      return undefined;
    }
    return { holder: value.holder, session_ref: value.session_ref, renewed_at: value.renewed_at };
  } catch {
    // 손상된 기록은 없는 것으로 본다. 그 값 때문에 아무 탭도 쓰지 못하는 편이 더 나쁘다.
    return undefined;
  }
}

export function createLease(options: LeaseOptions): Lease {
  const { storage, tabId, sessionRef, now } = options;

  function read(): LeaseRecord | undefined {
    try {
      return parse(storage.getItem(LEASE_KEY));
    } catch {
      return undefined;
    }
  }

  /** 적고 되읽어 확인한다. 조용히 무시하는 저장소에서 소유자가 되지 않게 한다. */
  function write(record: LeaseRecord): boolean {
    const text = JSON.stringify(record);
    try {
      storage.setItem(LEASE_KEY, text);
    } catch {
      return false;
    }
    const stored = read();
    return stored?.holder === record.holder && stored.renewed_at === record.renewed_at;
  }

  function expired(record: LeaseRecord | undefined, at: number): boolean {
    if (record === undefined) {
      return true;
    }
    /*
     * 시계가 뒤로 간 경우(기기 시각 변경)도 만료로 본다. 미래 시각이 적혀 있으면
     * 그 임차는 영원히 살아 있게 되고, 어느 탭도 쓰지 못한다.
     */
    const age = at - record.renewed_at;
    return age >= LEASE_TTL_MS || age < -LEASE_TTL_MS;
  }

  function mine(record: LeaseRecord | undefined): boolean {
    return record !== undefined && record.holder === tabId && record.session_ref === sessionRef;
  }

  return {
    acquire() {
      const at = now();
      const current = read();
      if (mine(current)) {
        return write({ holder: tabId, session_ref: sessionRef, renewed_at: at });
      }
      if (current !== undefined && current.session_ref === sessionRef && !expired(current, at)) {
        return false;
      }
      return write({ holder: tabId, session_ref: sessionRef, renewed_at: at });
    },

    renew() {
      const current = read();
      if (!mine(current)) {
        return false;
      }
      return write({ holder: tabId, session_ref: sessionRef, renewed_at: now() });
    },

    held() {
      const current = read();
      return mine(current) && !expired(current, now());
    },

    heldByOther() {
      const current = read();
      if (current === undefined || current.session_ref !== sessionRef) {
        return false;
      }
      return current.holder !== tabId && !expired(current, now());
    },

    release() {
      if (!mine(read())) {
        return;
      }
      try {
        storage.removeItem(LEASE_KEY);
      } catch {
        // 지우지 못해도 TTL이 지나면 다른 탭이 가져간다.
      }
    },
  };
}
